/**
 * TransactMoveOrderCreateModal.jsx
 * ======================
 * Transact Move Order Creation — dedicated, isolated chat modal.
 * AgentModalRouter.jsx routes here when agent_key === "transact-move-order".
 *
 * Mirrors the structure of SupplierAgentModal.jsx: self-contained chat shell
 * (pre-chat compact view -> chat mode), calling the agent's own dedicated
 * backend endpoint.
 *
 * WIZARD FIELDS: same pattern as SupplierAgentModal — the wizard schema is
 * fetched from /erp-agents/agent-fields/transact-transact-move-order by WizardFields
 * itself. In chat mode we render a 60% chat / 40% wizard split; in pre-chat
 * mode the wizard sits above the input so the user can see/fill fields
 * before the conversation even starts. Values captured by the backend
 * during chat (data.fields_collected) are merged into fieldValues so the
 * panel fills in live as the conversation progresses — mirroring the
 * CHAT_TO_WIZARD merge logic in SupplierAgentModal.
 *
 * OPTIMISTIC WIZARD FILL: in addition to the backend's fields_collected
 * merge, clicking a ui_options radio button fills the corresponding
 * wizard field immediately (see OPTION_FIELD_TO_WIZARD_KEY / the
 * onOptionClick handler below). This doesn't wait on a round trip, and
 * doesn't depend on the backend route assembling fields_collected --
 * it uses the exact value the user picked, which is already known
 * client-side at click time. Typed values (Quantity, Required Date)
 * still come from data.fields_collected, since there's no click event
 * to hook for free-text answers.
 *
 * PICK SLIP LINE EDITOR: when the backend sends structured
 * `pickslip_lines_ui` on a message (see node.py's _pickslip_lines_to_ui),
 * each line is rendered with its own quantity input and a per-line
 * Confirm button (see PickSlipLinesEditor below), instead of the old
 * plain-text numbered listing. Pressing Confirm on a line sends a plain
 * chat message ("Line N quantity Q") through the normal send pipeline —
 * the backend's extract_parameters/process_line_selection already knows
 * how to parse that shape and will select + quantify the line in one
 * shot.
 *
 * SESSION LIFECYCLE: every time this modal is opened (mounted), a brand
 * new session id is generated and the chat starts empty in the pre-chat
 * view. There is no "restore the last session" behavior and no "New Chat"
 * button — a fresh session is simply the default every time.
 *
 * CLOSING THE MODAL: only the X button (top-right) closes the modal.
 * Clicking the backdrop (the dark overlay outside the modal) no longer
 * closes it — this was previously wired up but caused the modal to close
 * unintentionally on stray touches/clicks near the edge, especially on
 * mobile/tablet. If backdrop-click-to-close is wanted back in the future,
 * it needs a more deliberate implementation (e.g. only closing on a true
 * mousedown+mouseup on the backdrop itself, not a drag that ends there).
 *
 * Backend endpoint: POST /erp-agents/transact-move-order-create/chat
 *   body: { session_id, message }
 */

import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Gear, Sparkle, PaperPlaneRight, User as UserIcon, Robot,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import WizardFields from "@/components/erp/WizardFields";

// ── Helpers ──────────────────────────────────────────────────────────────────

function ts() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ── Fallback option detection ──────────────────────────────────────────────
// These are stopgaps for turns where the backend hasn't been updated yet to
// send structured next_field_ui — prefer fixing the backend when possible,
// since text parsing is brittle (breaks on phrasing/format changes). When
// msg.result?.next_field_ui IS present, none of this runs (see ChatBubble).

// Detects "1. label" / "* label" / "- label" / "• label" list blocks (used
// for item search results, subinventory options, etc.) and returns both the
// parsed options AND the text with those list lines removed, so the bubble
// doesn't show the same list twice (once as text, once as buttons).
function extractListOptions(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const matches = [];
  lines.forEach((line, idx) => {
    const m = line.trim().match(/^(?:[*\-•]|\d+\.)\s+(.+)$/);
    if (m) matches.push({ idx, content: m[1].trim() });
  });

  // Require at least 2 matched lines to treat this as a selectable option
  // list rather than an incidental single numbered/bulleted sentence.
  if (matches.length < 2) return null;

  const options = matches.map(({ content }) => {
    // Rows look like "12040-09-1-000-0143 - 112/P9-PIPE CAM SHAFT..." or
    // "BNR-STG - BNR-Staging". Use the leading token before " - " as the
    // value when it looks like a code; otherwise use the full line.
    const dashSplit = content.split(" - ");
    const looksLikeCode = /^[A-Za-z0-9][A-Za-z0-9\-\/]*$/.test((dashSplit[0] || "").trim());
    const value = looksLikeCode && dashSplit.length > 1 ? dashSplit[0].trim() : content;
    return { label: content, value };
  });

  const matchedIdx = new Set(matches.map((m) => m.idx));
  const strippedText = lines
    .filter((_, idx) => !matchedIdx.has(idx))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return { options, strippedText };
}

// Detects a "(Yes/No)" style confirmation question, e.g. the final
// "Do you want me to create this Move Order? (Yes/No)" prompt.
function extractYesNo(text) {
  if (!text) return null;
  if (!/\(\s*yes\s*\/\s*no\s*\)/i.test(text)) return null;
  return [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];
}

// Detects the "add another item, or are you finished adding lines?" prompt.
function extractAddOrFinish(text) {
  if (!text) return null;
  const hasAdd = /add another/i.test(text);
  const hasFinish = /(finish(ed)?\s+adding|done adding|are you finished)/i.test(text);
  if (!hasAdd || !hasFinish) return null;
  return [
    { label: "➕ Add another item", value: "add another item" },
    { label: "✅ Finish adding", value: "finish adding" },
  ];
}

// Runs the detectors in priority order and returns { fieldUi, displayText }
// or null if nothing matched. Order matters: a Yes/No confirmation can
// appear alongside a numbered order summary (not meant to be clickable), so
// Yes/No and add/finish are checked before the generic list extractor.
function deriveFieldUi(text) {
  const yesNo = extractYesNo(text);
  if (yesNo) {
    return {
      fieldUi: { field: "parsed_confirm", uiType: "confirm", options: yesNo },
      displayText: text,
    };
  }

  const addFinish = extractAddOrFinish(text);
  if (addFinish) {
    return {
      fieldUi: { field: "parsed_add_finish", uiType: "action", options: addFinish },
      displayText: text,
    };
  }

  const list = extractListOptions(text);
  if (list) {
    return {
      fieldUi: { field: "parsed_list", uiType: "radio", options: list.options },
      displayText: list.strippedText || text,
    };
  }

  return null;
}

// Fields the wizard captures whose backend key differs from the wizard's
// schema field name — mirrors SupplierAgentModal's CHAT_TO_WIZARD map. Left
// empty by default (transact-move-order's fields_collected keys are expected
// to already line up with the wizard schema field names); add entries here
// if/when the backend starts sending differently-named keys.
const CHAT_TO_WIZARD = {};

// Maps a ui_field name (sent by the backend on ui_options responses, e.g.
// "OrganizationCode", "ItemNumber", "SourceSubinventory") to the wizard
// panel's field key, for the OPTIMISTIC fill described above. Only real
// backend field names appear here — never the synthetic "parsed_*" fields
// used by the text-parsing fallback, and never "LineAction" /
// "OrderConfirmation" (those are flow-control clicks, not data fields with
// a slot in the wizard). Assumed 1:1 with wizard schema keys, same
// assumption CHAT_TO_WIZARD documents above; update both together if that
// assumption turns out to be wrong for a given field.
const OPTION_FIELD_TO_WIZARD_KEY = {
  OrganizationCode: "OrganizationCode",
  ItemNumber: "ItemNumber",
  SourceSubinventory: "SourceSubinventory",
  DestinationSubinventory: "DestinationSubinventory",
  SourceLocator: "SourceLocator",
  DestinationLocator: "DestinationLocator",
};

// NOTE: session-restore-on-reopen (sessionStorage-backed) and the "New
// Chat" button have been removed — every time this modal is opened it
// always starts a brand-new session (see MoveOrderCreateModal below).

// ── Markdown renderer for assistant messages ──────────────────────────────────

function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />,
          li: ({ node, ...props }) => <li className="text-sm" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...props} />,
          code: ({ node, inline, ...props }) =>
            inline
              ? <code className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded" {...props} />
              : <code className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto" {...props} />,
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-2">
              <table className="w-full text-xs border-collapse" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-[#0020C2]/5" {...props} />,
          th: ({ node, ...props }) => (
            <th className="border border-gray-200 px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[10px] text-gray-600" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="border border-gray-200 px-2 py-1.5 text-gray-800" {...props} />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

// ── Option renderer — pills / dropdown / radio / confirm / action ─────────────
// uiType: "radio"    -> stacked full-width rows with a radio indicator
//                        (best for longer labels, e.g. item search results,
//                        subinventory options)
// uiType: "dropdown" -> stacked pill list, one per line
// uiType: "confirm"  -> side-by-side Yes/No buttons (Yes filled, No outline)
// uiType: "action"   -> side-by-side action buttons, e.g. add another item
//                        vs finish adding
// default            -> wrapped inline pills

function OptionButtons({ fieldUi, onPick }) {
  const options = fieldUi.options || [];
  if (!options.length) return null;

  if (fieldUi.uiType === "confirm") {
    return (
      <div className="mt-2 flex gap-2" data-testid={`chat-field-ui-${fieldUi.field}`}>
        {options.map((opt) => {
          const isYes = /^y/i.test(opt.value);
          return (
            <button
              key={opt.value}
              onClick={() => onPick(opt)}
              className={`text-xs font-semibold px-4 py-1.5 rounded-full transition-colors
                ${isYes
                  ? "bg-[#0020C2] text-white hover:bg-[#001AA3]"
                  : "border border-gray-300 text-gray-600 hover:bg-gray-50"}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  }

  if (fieldUi.uiType === "action") {
    return (
      <div className="mt-2 flex flex-wrap gap-2" data-testid={`chat-field-ui-${fieldUi.field}`}>
        {options.map((opt, i) => (
          <button
            key={opt.value}
            onClick={() => onPick(opt)}
            className={`text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors
              ${i === 0
                ? "bg-[#0020C2]/10 text-[#0020C2] border border-[#0020C2]/30 hover:bg-[#0020C2]/20"
                : "bg-gray-900 text-white hover:bg-gray-800"}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  if (fieldUi.uiType === "radio") {
    return (
      <div
        className="mt-2 flex flex-col gap-1.5"
        data-testid={`chat-field-ui-${fieldUi.field}`}
      >
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onPick(opt)}
            className="flex items-start gap-2.5 text-left text-xs px-3 py-2 border border-gray-200
              rounded-lg hover:border-[#0020C2] hover:bg-[#0020C2]/5 transition-colors group"
          >
            <span className="mt-0.5 shrink-0 w-3.5 h-3.5 rounded-full border-2 border-gray-300
              group-hover:border-[#0020C2] transition-colors" />
            <span className="text-gray-800 leading-snug">{opt.label}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div
      className={fieldUi.uiType === "dropdown" ? "mt-2 flex flex-col gap-1" : "mt-2 flex flex-wrap gap-1.5"}
      data-testid={`chat-field-ui-${fieldUi.field}`}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onPick(opt)}
          className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors text-left"
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Pick slip line editor — inline qty input + per-line confirm button ────────
// Renders each structured pick slip line (see node.py's
// _pickslip_lines_to_ui) as its own row: item/qty/from->to summary, a
// quantity input, and a Confirm button. Pressing Confirm sends
// "Line {n} quantity {qty}" as a normal chat message — the backend's
// extract_parameters + process_line_selection already parse that shape
// and will select + quantify the line in one round trip.

// ── Pick slip line editor — inline qty inputs + one shared confirm button ────
// Renders each structured pick slip line (see node.py's
// _pickslip_lines_to_ui) as its own row: item/qty/from->to summary and a
// quantity input. A single "Confirm" button sits below the list, enabled
// as soon as at least one line has a quantity typed in — the user doesn't
// have to fill in every line before confirming. Pressing it sends all
// filled-in lines together as one plain chat message
// ("Line 1 quantity 5, Line 3 quantity 2") — the backend's
// extract_parameters + process_line_selection already parse multiple
// "Line N quantity Q" clauses in a single message and will select +
// quantify every one of them in one round trip.

// ── Pick slip line editor — inline qty inputs + one shared confirm button ────
// Renders each structured pick slip line (see node.py's
// _pickslip_lines_to_ui) as its own row: item/qty/from->to summary and a
// quantity input. A single "Confirm" button sits below the list, enabled
// once at least one line has a quantity typed in AND every quantity that
// has been typed in is valid (>= 0 and <= that line's available/requested
// quantity) — the user doesn't have to fill in every line before
// confirming, but any invalid entry blocks the button until it's fixed.
// Pressing it sends all filled-in lines together as one plain chat message
// ("Line 1 quantity 5, Line 3 quantity 2") — the backend's
// extract_parameters + process_line_selection already parse multiple
// "Line N quantity Q" clauses in a single message and will select +
// quantify every one of them in one round trip.

// A line has no known upper bound if requested_quantity is missing or not
// a plain number (e.g. "?" placeholder) — in that case only the >= 0 rule
// applies.
function _lineMaxQty(line) {
  const raw = line.requested_quantity;
  if (raw === null || raw === undefined || raw === "") return null;
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
}

function _qtyValidity(line, rawValue) {
  if (rawValue === undefined || rawValue === "") return "empty";
  const num = Number(rawValue);
  if (Number.isNaN(num) || num < 0) return "invalid";
  const max = _lineMaxQty(line);
  if (max !== null && num > max) return "invalid";
  return "valid";
}

function PickSlipLinesEditor({ lines, onConfirmLines, disabled }) {
  const [quantities, setQuantities] = React.useState({});

  if (!lines?.length) return null;

  const handleChange = (lineNumber, value) => {
    setQuantities((prev) => ({ ...prev, [lineNumber]: value }));
  };

  const entryStatuses = lines.map((line) => ({
    line,
    raw: quantities[line.line_number],
    status: _qtyValidity(line, quantities[line.line_number]),
  }));

  const filledEntries = entryStatuses
    .filter(({ status }) => status === "valid")
    .map(({ line, raw }) => ({ line, qty: raw }));

  const hasAnyFilled = filledEntries.length > 0;
  const hasInvalidEntry = entryStatuses.some(({ status }) => status === "invalid");

  const canConfirm = hasAnyFilled && !hasInvalidEntry;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirmLines?.(filledEntries);
  };

  return (
    <div className="mt-2 flex flex-col gap-2" data-testid="pickslip-lines-editor">
      {entryStatuses.map(({ line, raw, status }) => (
        <div
          key={line.line_number}
          className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50"
        >
          <div className="flex-1 text-xs text-gray-800 leading-snug">
            <span className="font-semibold">
              {line.line_number}. {line.item}
            </span>
            <span className="text-gray-500">
              {" "}
              ({line.requested_quantity ?? "?"} {line.uom}) · {line.source} → {line.destination}
            </span>
          </div>
          <input
            type="number"
            min="0"
            max={_lineMaxQty(line) ?? undefined}
            step="any"
            value={raw ?? ""}
            onChange={(e) => handleChange(line.line_number, e.target.value)}
            placeholder="Qty"
            disabled={disabled}
            data-testid={`pickslip-line-qty-${line.line_number}`}
            title={
              status === "invalid"
                ? `Enter a quantity between 0 and ${line.requested_quantity ?? "the available quantity"}`
                : undefined
            }
            className={`w-16 text-xs border rounded px-2 py-1 outline-none disabled:opacity-50
              ${status === "invalid"
                ? "border-red-400 focus:border-red-500"
                : "border-gray-300 focus:border-[#0020C2]"}`}
          />
        </div>
      ))}

      <button
        onClick={handleConfirm}
        disabled={disabled || !canConfirm}
        data-testid="pickslip-lines-confirm"
        title={
          hasInvalidEntry
            ? "Fix the highlighted quantity before confirming"
            : undefined
        }
        className="self-end text-xs font-semibold px-4 py-1.5 rounded-full bg-[#0020C2] text-white
          hover:bg-[#001AA3] disabled:opacity-40 transition-colors"
      >
        Confirm
      </button>
    </div>
  );
}

// ── Chat bubble ─────────────────────────────────────────────────────────────

function ChatBubble({ msg, onOptionClick, onConfirmLines, busy }) {
  const isUser = msg.role === "user";

  // Prefer structured next_field_ui from the backend — it's shown as-is with
  // the full message text. If it's absent, fall back to detecting lists,
  // Yes/No confirmations, and add-another/finish prompts out of the plain
  // text, and strip any matched list lines so they aren't shown twice
  // (once as text, once as buttons).
  //
  // NOTE: if this message carries structured pickslip_lines_ui, we skip the
  // text-parsing fallback entirely — the backend no longer bakes the line
  // listing into the text for these turns, so there's nothing useful for
  // extractListOptions to find, and we don't want it accidentally matching
  // unrelated numbered sentences.
  const { fieldUi, displayText } = React.useMemo(() => {
    if (isUser || msg.loading) return { fieldUi: null, displayText: msg.text };

    // Real backend contract: node.py returns ui_options / ui_option_type /
    // ui_field directly on the response (not nested under next_field_ui).
    // ui_option_type "yes_no" -> confirm styling, everything else -> radio.
    if (msg.result?.ui_options?.length) {
      let uiType = "radio";
      if (msg.result.ui_option_type === "yes_no") uiType = "confirm";
      else if (msg.result.ui_field === "LineAction") uiType = "action";
      return {
        fieldUi: {
          field: msg.result.ui_field || "ui_selection",
          uiType,
          options: msg.result.ui_options,
        },
        displayText: msg.text,
      };
    }

    if (msg.result?.pickslip_lines_ui?.length) {
      return { fieldUi: null, displayText: msg.text };
    }

    // Fallback for older/unmigrated turns that only send plain text.
    const derived = deriveFieldUi(msg.text);
    return derived || { fieldUi: null, displayText: msg.text };
  }, [isUser, msg.loading, msg.result, msg.text]);

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white
        ${isUser ? "bg-[#0020C2]" : "bg-gray-700"}`}>
        {isUser ? <UserIcon size={11} weight="bold" /> : <Robot size={11} weight="bold" />}
      </div>

      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed
        ${isUser
          ? "bg-[#0020C2] text-white rounded-tr-sm"
          : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm"}`}>

        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}

        {!msg.loading && displayText && (
          isUser
            ? <p className="whitespace-pre-wrap">{displayText}</p>
            : <ChatMarkdown>{displayText}</ChatMarkdown>
        )}

        {fieldUi && (
          <OptionButtons
            fieldUi={fieldUi}
            onPick={(opt) => onOptionClick?.(opt, fieldUi.field)}
          />
        )}

        {!msg.loading && msg.result?.pickslip_lines_ui?.length > 0 && (
          <PickSlipLinesEditor
            lines={msg.result.pickslip_lines_ui}
            onConfirmLines={onConfirmLines}
            disabled={busy}
          />
        )}

        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>
            {msg.timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Input bar ─────────────────────────────────────────────────────────────────

function InputBar({ inputRef, value, onChange, onKeyDown, onSend, busy, placeholder }) {
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50
        focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder={placeholder}
          data-testid="agent-prompt"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none outline-none
            placeholder-gray-400 disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }}
        />
        <button
          onClick={onSend}
          disabled={busy || !value.trim()}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center
            hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0">
          {busy
            ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">↵ send · shift+↵ new line</span>
    </div>
  );
}

// ── Main Move Order Create Modal ───────────────────────────────────────────────

export default function MoveOrderCreateModal({ onClose }) {
  const { openAgent, setOpenAgent, setSetupOpen, setPendingAgent, profile, agents } = useErp();
  const agent = agents.find((a) => a.key === openAgent);

  // Session lifecycle — every time this modal is opened (mounted), it
  // starts a brand-new session. There is no restore-previous-session
  // behavior anymore (that used to read/write sessionStorage under
  // SESSION_STORAGE_KEY) and no "New Chat" button, since a fresh session
  // id is generated unconditionally here on mount.
  const [sessionId] = React.useState(() => crypto.randomUUID());

  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [chatStarted, setChatStarted] = React.useState(false);

  // Wizard fields state — mirrors SupplierAgentModal's fieldValues/missingFields.
  // WizardFields itself fetches the schema from
  // /erp-agents/agent-fields/move-order; here we just hold the values
  // and merge in whatever the backend reports as captured during chat, PLUS
  // whatever the user has picked via radio buttons this session (see
  // OPTION_FIELD_TO_WIZARD_KEY / sendMessage below).
  const [fieldValues, setFieldValues] = React.useState({});
  const [missingFields, setMissingFields] = React.useState([]);

  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Closing the modal (X button only — clicking the backdrop no longer
  // closes it, see the outer overlay div's onClick below) just tears the
  // modal down. Every future open creates its own fresh session id (see
  // useState above), so there's nothing to clean up here.
  const close = () => {
    setOpenAgent(null);
    onClose?.();
  };

  const openProfile = () => {
    setPendingAgent(openAgent);
    close();
    setSetupOpen(true);
  };

  // ── Send message — calls the dedicated Move Order Creation chat endpoint ──
  // optionField (new): when the message originated from a radio-button
  // click on a real backend field (see OPTION_FIELD_TO_WIZARD_KEY), the
  // wizard panel is filled with the picked value immediately, before the
  // network call even resolves. This does not replace the fields_collected
  // merge below — it's a same-tick fill for the case a click covers, since
  // there's no wait needed: the value the user picked IS the field value.
  const sendMessage = async (text, uiSelectedField, optionField) => {
    const userText = (text || "").trim();
    if (!userText || busy) return;
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (!chatStarted) setChatStarted(true);

    // Optimistic wizard fill for radio-button selections on known fields.
    if (optionField && OPTION_FIELD_TO_WIZARD_KEY[optionField]) {
      const wizardKey = OPTION_FIELD_TO_WIZARD_KEY[optionField];
      setFieldValues((prev) => ({ ...prev, [wizardKey]: userText }));
    }

    const now = ts();
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), role: "user", text: userText, timestamp: now },
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);

    try {
      const res = await api.post("/erp-agents/transact-move-order/chat", {
        session_id: sessionId,
        question: userText,
        // Tells the backend this value came from a button click on the
        // ui_options it generated (see node.py's classify_message /
        // extract_parameters bypass), so it's routed as an exact validated
        // value instead of being re-parsed as free text.
        ...(uiSelectedField ? { ui_selected_field: uiSelectedField } : {}),
      });
      const data = res.data;
      const reply = data?.reply || data?.answer || data?.message || "Done.";

      // Sync any fields the backend reports as captured into the wizard
      // panel — same idea as SupplierAgentModal's fields_collected merge,
      // kept generic here since move-order's schema is simpler.
      // This is still the source of truth for typed values (Quantity,
      // Required Date) that have no click event to hook. It layers on top
      // of (and can correct) the optimistic fill above.
      if (data?.fields_collected && typeof data.fields_collected === "object") {
        setFieldValues((prev) => {
          const merged = { ...prev };
          for (const [key, val] of Object.entries(data.fields_collected)) {
            if (val === null || val === undefined || val === "") continue;
            const wizardKey = CHAT_TO_WIZARD[key] || key;
            merged[wizardKey] = val;
          }
          return merged;
        });
      }
      if (Array.isArray(data?.missing_fields)) {
        setMissingFields(data.missing_fields);
      }

      const now2 = ts();
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, text: reply, result: data, timestamp: now2 }
            : m
        )
      );
    } catch (e) {
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, timestamp: ts(), text: `Error: ${e?.response?.data?.detail || e.message}` }
            : m
        )
      );
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @keyframes chatFadeIn    { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes wizardSlideIn { from { opacity:0; transform:translateX(20px);  } to { opacity:1; transform:translateX(0); } }
        .modal-pre  { width:672px; max-width:calc(100vw - 2rem); max-height:90vh; }
        .modal-chat { width:min(92vw,1400px); height:90vh; max-width:98vw; max-height:96vh; min-width:640px; min-height:460px; }
      `}</style>

      <div
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      >
        <div
          data-testid={`agent-modal-${openAgent}`}
          className={`bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden
            ${chatStarted ? "modal-chat" : "modal-pre"}`}
          style={{ transition: chatStarted ? "none" : "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle Fusion ERP · Vaku AI
              </div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5">{agent?.label}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{agent?.hint}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={openProfile}
                data-testid="agent-open-profile"
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"
              >
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} data-testid="agent-modal-close">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          {!chatStarted ? (

            /* Pre-chat — intro + wizard fields + input to start chatting */
            <>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                
                <WizardFields
                  agentKey={openAgent}
                  values={fieldValues}
                  onChange={setFieldValues}
                  missingFields={missingFields}
                  onCreateNew={() => {}}
                  fieldErrors={{}}
                  fieldWarnings={{}}
                />
              </div>
              <InputBar
                inputRef={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSend={() => sendMessage(input)}
                busy={busy}
                placeholder={agent?.hint || "Type a message…"}
              />
            </>

          ) : (

            /* Chat mode — 60% chat / 40% wizard, same split as SupplierAgentModal */
            <div className="flex flex-1 overflow-hidden">

              {/* LEFT — chat */}
              <div
                className="flex flex-col overflow-hidden border-r border-gray-200"
                style={{ width: "60%", animation: "chatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}
              >
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                  {messages.map((msg) => (
                    <ChatBubble
                      key={msg.id}
                      msg={msg}
                      busy={busy}
                      onOptionClick={(opt, field) =>
                        sendMessage(
                          opt.value,
                          // Only forward real backend field names — not the
                          // synthetic ones used by the text-parsing fallback
                          // (parsed_list / parsed_confirm / parsed_add_finish),
                          // since the backend doesn't know those.
                          field && !field.startsWith("parsed_") ? field : undefined,
                          // optionField for the optimistic wizard fill —
                          // same real-field-name guard as above, since
                          // OPTION_FIELD_TO_WIZARD_KEY only knows real
                          // backend field names too.
                          field && !field.startsWith("parsed_") ? field : undefined
                        )
                      }
                      onConfirmLines={(entries) =>
                        sendMessage(
                          entries
                            .map(({ line, qty }) => `Line ${line.line_number} quantity ${qty}`)
                            .join(", ")
                        )
                      }
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <InputBar
                  inputRef={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onSend={() => sendMessage(input)}
                  busy={busy}
                  placeholder="Continue the conversation…"
                />
              </div>

              {/* RIGHT — wizard fields */}
              <div
                className="flex flex-col bg-white"
                style={{ width: "40%", minWidth: "360px", animation: "wizardSlideIn 0.45s cubic-bezier(0.4,0,0.2,1)" }}
              >
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
                    <Sparkle size={11} weight="fill" /> Wizard fields
                    <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">
                      · auto-filled as you chat
                    </span>
                  </div>
                  <WizardFields
                    agentKey={openAgent}
                    values={fieldValues}
                    onChange={setFieldValues}
                    missingFields={missingFields}
                    onCreateNew={() => {}}
                    fieldErrors={{}}
                    fieldWarnings={{}}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}